// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useContext } from "react"
import { useSelector } from "react-redux"
import { useKeyPressEvent } from "react-use"
import { useHistory } from "react-router-dom"
import { ThemeContext } from "styled-components/macro"
import * as TimelineSlice from "../slices/timeline"
import {
  convertTimelineDateToTimelinePathDate,
  getCurrentDate,
  getWeekRange,
  TimelineDate,
  toDate,
  TODAY_DATE_PARAM,
  toTimelineDate,
} from "features/timeline/dateTime"
import { DateArrowLink, IconArrowLeft, IconArrowRight } from "../../shared/styles"
import { ROUTE_PATH } from "app/routing"
import { isDisabledShortcut } from "../../../lib/utils"
import { useWindowSize } from "../../../app/useWindowSize"

const Day = 1
const Week = 7
type NavStep = typeof Day | typeof Week

type Props = {
  toPrevDate: boolean,
}

const getNavPath = (navDate: TimelineDate): string => {
  if (getCurrentDate() === navDate) {
    return ROUTE_PATH.TIMELINE.path.replace(":date?", TODAY_DATE_PARAM)
  }
  return ROUTE_PATH.TIMELINE.path.replace(
    ":date?",
    convertTimelineDateToTimelinePathDate(navDate),
  )
}

const NavArrow = ({ toPrevDate }: Props): React.ReactElement<Props> => {
  const activeDate = toDate(useSelector(TimelineSlice.selectActiveDate))

  const { width } = useWindowSize()
  const theme = useContext(ThemeContext)

  const getNavDate = (navStep: NavStep): TimelineDate => {
    const navDate = toPrevDate
      ? activeDate.minus({ days: navStep })
      : activeDate.plus({ days: navStep })

    if (navStep === Week) {
      const currentDate = getCurrentDate()
      const navWeek = getWeekRange(navDate)
      if (navWeek.startDateLocal <= currentDate && currentDate <= navWeek.endDateLocal) {
        // navigate to current day if we open current week
        return currentDate
      }
    }
    return toTimelineDate(navDate)
  }

  const history = useHistory()
  // eslint-disable-next-line lingui/no-unlocalized-strings
  const key = toPrevDate ? "ArrowLeft" : "ArrowRight"
  useKeyPressEvent(key, undefined, e => {
    if (isDisabledShortcut()) {
      return
    }
    const keyNavDate = getNavDate(e.shiftKey ? Week : Day)
    history.push(getNavPath(keyNavDate))
  })

  const btnNavDate = getNavDate(width > theme.screen.smNumeric ? Week : Day)
  const IconArrow = toPrevDate ? IconArrowLeft : IconArrowRight
  return (
    <DateArrowLink
      to={getNavPath(btnNavDate)}
    >
      <IconArrow/>
    </DateArrowLink>
  )
}

export default NavArrow
